import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { GiftingCartApiResponse, GiftingLocalCart } from "@swiggy-private/cart-types";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { CartActionTypes as GiftingCartActionTypes } from "@cart-client/reducers/CartReducer";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/cart-types";

/**
 * State shape for the gifting cart reducer.
 * - uniqueIdForCart: Unique identifier for the gifting cart session.
 * - localCart: Local (client-side) representation of the gifting cart.
 * - reviewedCart: Server-reviewed gifting cart state.
 */
export interface GiftingCartState {
    uniqueIdForCart: string;
    localCart: GiftingLocalCart | null;
    reviewedCart: GiftingReviewedCart | null;
}

/**
 * Action definitions for gifting cart reducer.
 */
export type GiftingCartAction =
    | { type: GiftingCartActionTypes.UPDATE_LOCAL_CART; payload: GiftingLocalCart }
    | { type: GiftingCartActionTypes.UPDATE_REVIEWED_CART; payload: GiftingReviewedCart }
    | {
          type: GiftingCartActionTypes.UPDATE_CART;
          payload: {
              uniqueIdForCart: string;
              localCart: GiftingLocalCart | null;
              reviewedCart: GiftingReviewedCart;
          };
      }
    | { type: GiftingCartActionTypes.CLEAR_LOCAL_CART; payload: { reviewedCart: GiftingReviewedCart } };

const prepareUpdateGiftingNativeBridgePayload = (state: GiftingCartState) => {
    return {
        ...state.localCart,
        // apps need entire payload from backend for metadata for each item
        // this is done as localCart for IM items is different hence backend needs lesser data compared to apps for IM items
        imCartItems: state.reviewedCart?.data?.imCartItems || [],
    };
};

/**
 * Reducer for managing gifting cart state.
 * Handles local and reviewed cart updates, and syncs with native bridge when needed.
 */
export function GiftingCartReducer(state: GiftingCartState, action: GiftingCartAction): GiftingCartState {
    const { localCart, reviewedCart } = state || {};
    const { type, payload } = action || {};

    switch (type) {
        case GiftingCartActionTypes.UPDATE_LOCAL_CART: {
            // Update only the local gifting cart and sync with bridge
            const updatedCart = {
                ...state,
                localCart: { ...localCart, ...payload },
            };
            getBridgeSdk().updateGiftingLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.GIFTING,
                    marketplaceCategory: MARKETPLACE_CATEGORY.GIFTING,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                },
                data: prepareUpdateGiftingNativeBridgePayload(updatedCart),
            });
            return updatedCart;
        }
        case GiftingCartActionTypes.UPDATE_REVIEWED_CART:
            // Update only the reviewed gifting cart
            return Object.assign({}, state, { reviewedCart: payload });
        case GiftingCartActionTypes.UPDATE_CART: {
            // Update both local and reviewed gifting cart, and sync with bridge
            getBridgeSdk().updateGiftingLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.GIFTING,
                    marketplaceCategory: MARKETPLACE_CATEGORY.GIFTING,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                },
                data: prepareUpdateGiftingNativeBridgePayload({
                    ...state,
                    ...payload,
                }),
            });

            return {
                ...state,
                ...payload,
            };
        }
        case GiftingCartActionTypes.CLEAR_LOCAL_CART:
            getBridgeSdk().clearGiftingLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.GIFTING,
                    marketplaceCategory: MARKETPLACE_CATEGORY.GIFTING,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                },
            });
            // Clear the local gifting cart, but retain reviewed cart (with possible update)
            return {
                ...state,
                localCart: null,
                reviewedCart: {
                    ...reviewedCart,
                    ...(payload as {
                        reviewedCart: GiftingReviewedCart;
                    }).reviewedCart,
                    data: {
                        ...(reviewedCart?.data as GiftingCartApiResponse),
                        // make items explicitly empty to render error for empty cart
                        foodCartItems: undefined,
                        imCartItems: undefined,
                    },
                },
            };
        default:
            throw new Error(`unhandled gifting cart action type ${type}`);
    }
}
